/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import Portal from '@atlaskit/portal';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { createPolarisStore } from '@atlassian/jira-polaris-lib-react-sweet-state-utils/src/utils/store/index.tsx';
import { createContainer, createHook, type Action } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';

type ListPortalProviderProps = {
	children: ReactNode;
	menuOrientation?: 'left' | 'default';
};

type State = {
	portalElement?: HTMLElement;
};

const actions = {
	setPortalElement:
		(portalElement: HTMLElement): Action<State> =>
		({ setState }) => {
			setState({ portalElement });
		},
} as const;

type Actions = typeof actions;

const ListPortalStore = createPolarisStore<State, Actions>({
	initialState: {
		portalElement: undefined,
	},
	actions,
	name: 'PolarisListPortalStore',
});

const usePortalElementHook = createHook(ListPortalStore, {
	selector: (state: State) => state.portalElement,
});

const useActions = createHook(ListPortalStore, {
	selector: null,
});

const ListPortalController = createContainer<State, Actions>(ListPortalStore);

const ListPortalHandler = ({ children, menuOrientation }: ListPortalProviderProps) => {
	const [, { setPortalElement }] = useActions();

	return (
		<>
			<Portal zIndex={layers.layer}>
				<div
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
					ref={setPortalElement as any}
					css={[
						menuOrientation === 'left' && fg('jpd_issues_relationships') && portalContentStyles,
					]}
				/>
			</Portal>
			{children}
		</>
	);
};

const portalContentStyles = css({
	// align to the left some of the menus that don't accept styles prop
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[class$="menu"]': {
		right: 0,
	},
});

export const ListPortalProvider = ({ children, menuOrientation }: ListPortalProviderProps) => (
	<ListPortalController>
		<ListPortalHandler menuOrientation={menuOrientation}>{children}</ListPortalHandler>
	</ListPortalController>
);

export const useListPortalElement = (): HTMLElement | undefined => {
	const [portalElement] = usePortalElementHook();
	return portalElement;
};
